/**
 * The Legacy/New choice for an incident, read from the URL or from what the user last picked, and written to both.
 */

import { useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';

import {
  INITIAL_RESPONSE_VIEW_PARAM,
  initialResponseViewStorageKey,
  parseInitialResponseView,
  resolveInitialResponseView,
  serializeInitialResponseView,
  type InitialResponseView,
} from 'utils/initialResponseView';

export function useInitialResponseView(): {
  view: InitialResponseView;
  setView: (next: InitialResponseView) => void;
} {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const storedValue = incidentId
    ? localStorage.getItem(initialResponseViewStorageKey(incidentId))
    : null;

  const paramValue = searchParams.get(INITIAL_RESPONSE_VIEW_PARAM);

  const view = resolveInitialResponseView({ paramValue, storedValue });

  /**
   * A pinned view is remembered, so it survives the in-app navigations that drop the query string. Without this,
   * following a link to one view would silently fall back to the other on the first tab change.
   */
  useEffect(() => {
    const pinned = parseInitialResponseView(paramValue);
    if (!incidentId || !pinned || pinned === storedValue) return;
    localStorage.setItem(
      initialResponseViewStorageKey(incidentId),
      serializeInitialResponseView(pinned)
    );
  }, [incidentId, paramValue, storedValue]);

  const setView = useCallback(
    (next: InitialResponseView) => {
      if (incidentId) {
        localStorage.setItem(
          initialResponseViewStorageKey(incidentId),
          serializeInitialResponseView(next)
        );
      }
      setSearchParams(
        (params) => {
          params.set(
            INITIAL_RESPONSE_VIEW_PARAM,
            serializeInitialResponseView(next)
          );
          return params;
        },
        { replace: true }
      );
    },
    [incidentId, setSearchParams]
  );

  return { view, setView };
}
